import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { Box } from "@mui/material";
import { MY_HR_UPDATE_USER_EMAIL } from "my-hr/graphql";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcEmailInputField,
  OcPasswordInputField,
} from "oc/components";
import {
  useOcCurrentUserState,
  useOcEmailInput,
  useOcPasswordInput,
  useOcProperty,
  useOcSnackbar,
} from "oc/hooks";

interface MyHrDataCardProps {
  userData: {
    _id?: string;
    email?: string;
    // phone?: string; // Uncomment and adjust if phone is used
    // Add other fields as needed
  };
  isMobile: boolean;
}

export const MyHrDataCard: React.FC<MyHrDataCardProps> = ({
  userData,
  isMobile,
}) => {
  const { t } = useTranslation();

  let ocCurrentUserState = useOcCurrentUserState();

  const { setOpenMessage } = useOcSnackbar();

  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { data: communicationChannel } = useOcProperty({
    resource: "system",
    key: "communicationChannel",
  });

  const { data: loginType } = useOcProperty({
    resource: "system",
    key: "loginType",
  });

  const {
    value: email,
    setValue: setEmail,
    isValid: isEmailValid,
    validate: validateEmail,
    error: emailError,
    setError: setEmailError,
    helperText: emailHelperText,
    setHelperText: setEmailHelperText,
  } = useOcEmailInput();

  /*const {
    value: phone,
    setValue: setPhone,
    isValid: isPhoneValid,
    validate: validatePhone,
  } = usePhoneInput({ stateId: "myProfilePhone" });*/

  const {
    value: password,
    setValue: setPassword,
    isValid: isPasswordValid,
    validate: validatePassword,
    error: passwordError,
    setError: setPasswordError,
    helperText: passwordHelperText,
    setHelperText: setPasswordHelperText,
  } = useOcPasswordInput();

  const [emailInputFilled, setEmailInputFilled] = useState(false);
  //const [phoneInputFilled, setPhoneInputFilled] = useState(false);

  useEffect(() => {
    if (userData?.email && !emailInputFilled) {
      setEmail(userData?.email ?? "");
      setEmailInputFilled(true);
    }

    /*if (userData?.phone && !phoneInputFilled) {
      setPhone(userData?.phone);
      setPhoneInputFilled(true);
    }*/
  }, [emailInputFilled, setEmail, userData?.email]);

  const [updateUserEmail] = useMutation(MY_HR_UPDATE_USER_EMAIL, {
    onCompleted: () => {
      setLoading(false);
      setPassword("");
      setOpenMessage({
        type: "success",
        message: t("OC_USER_UPDATED"),
      });
    },
    onError: (error) => {
      if (error.message === "Wrong password!") {
        setPasswordError(true);
        setPasswordHelperText(t("MY_HR_ERROR_MESSAGE_WRONG_PASSWORD"));
        setShowConfirmDialog(false);
      }
      setLoading(false);
    },
  });

  const handleCancel = () => {
    setEmail(userData?.email ?? "");
    //setPhone(userData?.phone);
    setPassword("");
    setPasswordError(false);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEmailValid()) {
      validateEmail();
      setShowConfirmDialog(false);
    }

    /*if (communicationChannel === "sms") {
        if (!isPhoneValid()) {
          validatePhone();
        }
    }*/

    if (!isPasswordValid({ checkPasswordPolicy: false })) {
      validatePassword({ checkPasswordPolicy: false });
      setShowConfirmDialog(false);
    }

    if (!isEmailValid()) return;
    if (!isPasswordValid({ checkPasswordPolicy: false })) return;

    //if (communicationChannel === "sms" && !isPhoneValid()) return;

    if (userData?.email === email && communicationChannel !== "sms") {
      return;
    }

    /*if (userData?.email === email && userData?.phone === phone) {
      return;
    }*/

    let queryParams = {
      _id: userData?._id,
      ldapUser: loginType === "sso" ? ocCurrentUserState?.ldapUser : "",
      email: email,
      password: password,
    };

    /*if (communicationChannel === "sms") {
      queryParams.phone = phone;
    }*/

    setLoading(true);
    updateUserEmail({ variables: queryParams });
    setShowConfirmDialog(false);
  };

  let disabled = userData?.email === email;
  /*if (communicationChannel === "sms" && userData?.phone !== phone) {
    disabled = false;
  }*/

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <Box>
      <OcCard label={t("MY_HR_USER_ACCOUNT")} loading={loading}>
        <OcConfirmationDialog
          showDialog={showConfirmDialog}
          onClose={() =>
            handleSave({ preventDefault: () => { } } as React.FormEvent)
          }
          onCancel={handleDialogCancel}
          title={t("OC_CONFIRM_SETTING")}
          texts={[t("OC_CONFIRM_SETTING_INFO")]}
          okButtonText={t("OC_ALL_RIGHT")}
          cancelButtonText={t("OC_CANCEL")}
        />
        <Box padding="16px" width={isMobile ? "auto" : "464px"}>
          <form>
            <OcEmailInputField
              value={email}
              setValue={setEmail}
              error={emailError}
              setError={setEmailError}
              helperText={emailHelperText}
              setHelperText={setEmailHelperText}
              label={t("MY_HR_EMAIL")}
            />
            {/*communicationChannel === "sms" && (
              <Box>
                <Box height="36px" />
                <OcPhoneInputField stateId="myProfilePhone" />
              </Box>
            )*/}
            <Box height="36px" style={{ borderBottom: "1px solid #C6CACC" }} />
            <Box height="36px" />
            <OcPasswordInputField
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              helperText={passwordHelperText}
              label={t("MY_HR_CURRENT_PASSWORD")}
              setError={setPasswordError}
              setHelperText={setPasswordHelperText}
            />
            <Box height="20px" />
          </form>
        </Box>
        <OcCardButtonBar
          handleRightButton={handleCancel}
          rightButtonColor="secondary"
          rightButtonText={t("OC_CANCEL")}
          handlePrimaryButton={() => setShowConfirmDialog(true)}
          primaryButtonText={t("OC_SAVE")}
          primaryButtonDisabled={disabled}
          primaryButtonId="edit-email-button"
        />
      </OcCard>
    </Box>
  );
};

export default MyHrDataCard;
